/**
 * 情报流（重写）。
 *
 * 旧实现：从 RSS 源（36氪/IT之家/CNBC 等）拉全量新闻，前端按关键词过滤。
 *         问题：RSS 源多数已失效，且过滤逻辑与后端不一致。
 *
 * 新实现：对每只已订阅股票调用后端 /api/news/preview，
 *         拿到与推送完全一致的「采集+过滤+去重+分析」后的新闻，
 *         保证页面所见即推送所得。只读，不写库。
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Newspaper, Bell, History, RefreshCw, Loader2, ExternalLink, Globe2 } from "lucide-react";
import { useApiStore } from "@/store/useApiStore";
import { NewsApi, type PreviewNewsItem, ApiError } from "@/services/api";
import { cn } from "@/lib/utils";
import { relTime, fmtDate } from "@/lib/format";

interface StockNews {
  stockCode: string;
  stockName: string;
  items: PreviewNewsItem[];
  error?: string;
}

export default function Feed() {
  const nav = useNavigate();
  const { subscriptions, history, initialized, refresh } = useApiStore();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<StockNews[]>([]);
  const [activeStock, setActiveStock] = useState<string>("all");

  useEffect(() => {
    if (!initialized) refresh();
  }, [initialized, refresh]);

  const fetchAll = async () => {
    if (subscriptions.length === 0) {
      setData([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const results = await Promise.all(
      subscriptions.map(async (s) => {
        try {
          const r = await NewsApi.preview(s.stockCode, s.stockName, s.mktNum, 15);
          return {
            stockCode: s.stockCode,
            stockName: s.stockName,
            items: r.news,
            error: r.ok ? undefined : r.message,
          } as StockNews;
        } catch (e) {
          return {
            stockCode: s.stockCode,
            stockName: s.stockName,
            items: [],
            error: e instanceof ApiError ? e.message : "加载失败",
          } as StockNews;
        }
      }),
    );
    setData(results);
    setLoading(false);
  };

  useEffect(() => {
    if (initialized) fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, subscriptions.map((s) => s.stockCode).join(",")]);

  const allItems = useMemo(() => {
    const list: (PreviewNewsItem & { stockCode: string; stockName: string })[] = [];
    data.forEach((d) => {
      d.items.forEach((it) => list.push({ ...it, stockCode: d.stockCode, stockName: d.stockName }));
    });
    list.sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
    return list;
  }, [data]);

  const filtered = activeStock === "all"
    ? allItems
    : allItems.filter((x) => x.stockCode === activeStock);

  return (
    <div className="p-4 md:p-6 max-w-[1200px] mx-auto">
      <header className="mb-5 flex items-end justify-between flex-wrap gap-3 animate-riseIn">
        <div>
          <h1 className="font-serif text-2xl text-ink-100 mb-1 flex items-center gap-2 title-bar">
            <Newspaper size={22} className="text-gold" />
            我的情报流
          </h1>
          <p className="text-sm text-ink-400">
            每只订阅股票的增量情报（与推送内容一致，只读预览）
          </p>
        </div>
        <button
          onClick={fetchAll}
          disabled={loading || subscriptions.length === 0}
          className="btn-ghost text-xs"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> 刷新
        </button>
      </header>

      {subscriptions.length === 0 ? (
        <div className="card p-10 text-center animate-riseIn">
          <Bell size={32} className="text-ink-600 mx-auto mb-3" />
          <div className="text-ink-200 mb-2 font-serif text-lg">尚未订阅任何股票</div>
          <p className="text-sm text-ink-400 mb-5 max-w-md mx-auto">
            订阅股票后，这里将展示每只股票的增量情报 ——
            内容与实际推送到微信的完全一致。
          </p>
          <button onClick={() => nav("/subscription")} className="btn-gold">
            去添加订阅
          </button>
        </div>
      ) : (
        <>
          {/* 股票切换 */}
          <div className="flex flex-wrap gap-2 mb-5 animate-riseIn">
            <StockChip
              active={activeStock === "all"}
              onClick={() => setActiveStock("all")}
              label={`全部 (${allItems.length})`}
            />
            {subscriptions.map((s) => {
              const cnt = data.find((d) => d.stockCode === s.stockCode)?.items.length ?? 0;
              return (
                <StockChip
                  key={s.stockCode}
                  active={activeStock === s.stockCode}
                  onClick={() => setActiveStock(s.stockCode)}
                  label={`${s.stockName} (${cnt})`}
                />
              );
            })}
          </div>

          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="card p-4">
                  <div className="skeleton h-3 w-32 mb-3 rounded" />
                  <div className="skeleton h-4 w-4/5 mb-2 rounded" />
                  <div className="skeleton h-3 w-full rounded" />
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="card p-10 text-center text-sm text-ink-400 animate-riseIn">
              {activeStock === "all"
                ? "所有股票当前均无增量情报"
                : "该股票当前无增量情报"}
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((n, i) => (
                <PreviewCard key={`${n.stockCode}-${n.url}-${i}`} n={n} />
              ))}
            </div>
          )}
        </>
      )}

      {/* 推送历史时间轴（7天，统一收口于此） */}
      <section className="card p-5 mt-6 animate-riseIn">
        <h2 className="font-serif text-lg text-ink-100 flex items-center gap-2 mb-4 title-bar">
          <History size={16} className="text-gold" />
          推送历史
          <span className="text-xs text-ink-500 font-sans">最近 7 天</span>
        </h2>
        {history.length === 0 ? (
          <div className="text-center py-8 text-sm text-ink-400">暂无推送记录</div>
        ) : (
          <div className="relative pl-4">
            <div className="absolute left-[5px] top-1 bottom-1 w-px bg-ink-700" />
            <div className="space-y-3">
              {history.map((h, i) => {
                // 历史是「曾经推送过」的留痕，取消订阅不会删除历史；
                // 合并推送的 stockCode 是逗号拼接的多只股票，拆分后判断：
                // 仅当涉及的所有股票都已不在订阅列表时才标「已取消订阅」。
                const codes = h.stockCode.split(",").map((c) => c.trim()).filter(Boolean);
                const unsubscribed = codes.length > 0
                  && codes.every((c) => !subscriptions.some((s) => s.stockCode === c));
                return (
                <div key={i} className="relative">
                  <span className={cn(
                    "absolute -left-4 top-1.5 w-2.5 h-2.5 rounded-full border-2 border-ink-950",
                    h.success ? "bg-gold" : "bg-bear",
                  )} />
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="num text-xs text-ink-300">{fmtDate(h.time)}</span>
                    <span className="text-xs text-ink-200 truncate max-w-[60%]">{h.stockName}</span>
                    {h.success ? (
                      <span className="label-tag text-bull border-bull/40 bg-bull/10">{h.newsCount}条</span>
                    ) : (
                      <span className="label-tag text-bear border-bear/40 bg-bear/10">失败</span>
                    )}
                    {unsubscribed && (
                      <span className="label-tag text-ink-400 border-ink-600 bg-ink-800/60">已取消订阅</span>
                    )}
                  </div>
                  <div className="text-xs text-ink-400 mt-0.5 truncate">{h.title}</div>
                </div>
                );
              })}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function StockChip({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "px-3 py-1.5 text-xs border transition-colors",
        active
          ? "border-gold bg-gold/10 text-gold"
          : "border-ink-700 text-ink-400 hover:border-ink-600 hover:text-ink-200",
      )}
    >
      {label}
    </button>
  );
}

function PreviewCard({ n }: { n: PreviewNewsItem & { stockCode: string; stockName: string } }) {
  const tendency = n.tendency;
  const isIntl = n.sourceType === "international";
  return (
    <article className="card card-hover p-4 animate-riseIn">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="label-tag text-gold border-gold/40 bg-gold/10">
          {n.stockName}
        </span>
        {tendency === "bullish" && (
          <span className="label-tag text-bull border-bull/40 bg-bull/10">利多</span>
        )}
        {tendency === "bearish" && (
          <span className="label-tag text-bear border-bear/40 bg-bear/10">利空</span>
        )}
        <span className={cn(
          "label-tag",
          isIntl ? "text-sky-300 border-sky-400/40 bg-sky-400/10" : "text-ink-300 border-ink-600 bg-ink-800",
        )}>
          {isIntl && <Globe2 size={10} />}
          {isIntl ? "国际" : "国内"}
        </span>
        {n.impact && (
          <span className={cn(
            "label-tag",
            n.impact === "high"
              ? "text-bull border-bull/60 bg-bull/10"
              : n.impact === "medium"
                ? "text-gold border-gold/60 bg-gold/10"
                : "text-ink-300 border-ink-600 bg-ink-800",
          )}>
            {n.impact === "high" ? "高影响" : n.impact === "medium" ? "中影响" : "低影响"}
          </span>
        )}
        <span className="num text-[0.6875rem] text-ink-500 ml-auto">{relTime(n.publishedAt)}</span>
        <span className="text-[0.6875rem] text-ink-400">· {n.source}</span>
      </div>

      <h3 className="font-serif text-[0.9375rem] leading-snug text-ink-100 mb-1.5">
        {n.translation || n.title}
      </h3>
      {n.translation && n.translation !== n.title && (
        <p className="font-serif italic text-[0.8125rem] text-ink-400 mb-1.5">{n.title}</p>
      )}

      {n.summary && (
        <p className="text-[0.8125rem] leading-relaxed text-ink-300 mb-2">{n.summary}</p>
      )}

      {n.logic && (
        <p className="text-[0.75rem] text-gold/80 border-l-2 border-gold/30 pl-3 mb-2 leading-relaxed">
          💡 {n.logic}
        </p>
      )}

      <div className="flex items-center justify-between mt-2">
        <span className="num text-[0.625rem] text-ink-600">
          {new Date(n.publishedAt).toLocaleString("zh-CN")}
        </span>
        {n.url && (
          <a
            href={n.url}
            target="_blank"
            rel="noreferrer"
            className="text-[0.6875rem] text-ink-400 hover:text-gold flex items-center gap-1"
          >
            原文 <ExternalLink size={11} />
          </a>
        )}
      </div>
    </article>
  );
}
